/**
 * Rooms domain API functions (Step 7.1.14).
 *
 * List rooms (org-wide or per unit), delete, normalize shape.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  expectPaginatedItems,
  parseAdminErrorResponse,
} from "./core";

export function fetchAdminRooms(unitId = null) {
  const url = unitId
    ? `${API_BASE_URL}/api/admin/units/${encodeURIComponent(unitId)}/rooms`
    : `${API_BASE_URL}/api/admin/rooms`;
  return fetch(url, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error("Rooms konnten nicht geladen werden.");
    }
    return res.json();
  });
}

export async function deleteAdminRoom(roomId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rooms/${encodeURIComponent(roomId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  try {
    return await res.json();
  } catch {
    return { status: "ok" };
  }
}

export async function createAdminRoom(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/rooms`, {
    method: "POST",
    headers: { ...getApiHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function patchAdminRoom(roomId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rooms/${encodeURIComponent(roomId)}`,
    {
      method: "PATCH",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * Normalize room from API for pages that expect unitId, status, priceMonthly.
 * room.price / priceMonthly = planned target rent (Soll); actual rent is on tenancy.
 */
/**
 * GET /api/admin/rooms/picker-options (D4a2) — the ``{items, total}``
 * envelope the async pickers read, with the dependent parent filters.
 *
 * ``fetchAdminRooms`` above returns a bare unbounded array, which is
 * exactly what a picker must not do; this pages and searches instead.
 *
 * ``unitId`` / ``propertyId`` narrow the options to one unit or one
 * building. Only what is set is sent — an omitted filter means "search
 * every room in the org", which is a legitimate starting state and not
 * an error. The server rejects an unknown or cross-org id with 404 and a
 * unit/property pair that contradicts itself with 409, so a caller never
 * has to guess whether an empty page means "none" or "not allowed".
 */
export function fetchAdminRoomPickerOptions({
  q,
  unitId,
  propertyId,
  limit,
  offset,
} = {}) {
  const sp = new URLSearchParams();
  if (q != null && String(q).trim()) sp.set("q", String(q).trim());
  if (unitId) sp.set("unit_id", String(unitId));
  if (propertyId) sp.set("property_id", String(propertyId));
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/rooms/picker-options${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) {
      // Surface the server's own German detail (404 / 409) instead of a
      // generic message — "Die gewählte Unit gehört nicht zur gewählten
      // Liegenschaft." tells the user what to fix; "konnte nicht geladen
      // werden" does not.
      const message = await parseAdminErrorResponse(res);
      throw new Error(message || "Zimmer konnten nicht geladen werden.");
    }
    const data = await res.json();
    const items = expectPaginatedItems(
      data,
      "GET /api/admin/rooms/picker-options"
    );
    return {
      items,
      total: typeof data?.total === "number" ? data.total : items.length,
    };
  });
}

/**
 * GET /api/admin/rooms/{id} (D4c2) — the room detail read model.
 *
 * Carries the room's own fields, its unit and property (named, never as
 * bare ids) and its occupancy from the canonical backend rule.
 *
 * A 404 covers BOTH "no such room" and "belongs to another
 * organisation": the backend answers identically for the two on purpose,
 * and nothing here may widen that. ``err.status`` is attached so the page
 * can tell not-found from a real failure without parsing the message.
 */
export async function fetchAdminRoomById(roomId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rooms/${encodeURIComponent(roomId)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(
      (await parseAdminErrorResponse(res)) || "Zimmer konnte nicht geladen werden."
    );
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function normalizeRoom(r) {
  if (!r) return r;
  return {
    ...r,
    unitId: r.unitId ?? r.unit_id,
    priceMonthly: r.priceMonthly ?? r.price ?? r.base_rent_chf ?? 0,
    status:
      r.status ??
      (r.is_active === false ? "Inaktiv" : "Frei"),
    roomName: r.roomName ?? r.name,
    size_m2: r.size_m2 ?? null,
    minimumStayMonths:
      r.minimumStayMonths ?? r.default_minimum_stay_months ?? 3,
    noticePeriodMonths:
      r.noticePeriodMonths ?? r.default_notice_period_months ?? 1,
  };
}
